import { ROLES_EXPORT_COLUMN_KEYS } from "./roles.constants";
import type { HttpQueryParam, HttpRequestConfig, IHttpClient } from "@/core/interfaces";
import {
  extractItems,
  extractTotal,
  isRecord,
  readCount,
  readFirstText,
  readText,
  toPaginatedQuery,
} from "@/core/utils";
import {
  appendExportColumns,
  appendReportQuery,
  parseContentDispositionFileName,
} from "@/modules/export";
import type { ExportedFile } from "@/modules/export";
import { Registry } from "@/shared/infra/dependency-injection/registry";
import { DEFAULT_ROLE_SORT } from "./roles.sort";
import type {
  CreateRoleParams,
  DeleteRoleParams,
  ExportRolesParams,
  ListRolesParams,
  Role,
  RolePermission,
  RolesPage,
  UpdateRoleParams,
} from "./roles.types";

export const ROLE_PATHS = {
  LIST: "/api/plataforma/perfis",
  EXPORT: "/api/plataforma/perfis/exportar",
} as const;

export function buildRolePath(id: string): string {
  return `${ROLE_PATHS.LIST}/${encodeURIComponent(id)}`;
}

const COLLECTION_KEYS = ["perfis"];

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

function readFirstCount(raw: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    const count = readCount(raw[key]);
    if (count !== null) return count;
  }

  return null;
}

/**
 * Claims do perfil no contrato do bff: agrupadas por módulo
 * (`claims: [{ modulo, nome, claims: [{ claim, label }] }]`).
 */
function extractRolePermissions(raw: Record<string, unknown>): RolePermission[] {
  const groups = Array.isArray(raw.claims) ? raw.claims : [];

  return groups.flatMap((group) => {
    if (!isRecord(group) || !Array.isArray(group.claims)) return [];
    const modulo = readFirstText(group, "nome", "modulo");

    return group.claims.flatMap((item) => {
      if (!isRecord(item)) return [];
      const claim = readText(item.claim);
      if (!claim) return [];
      return [{ id: claim, codigo: claim, nome: readText(item.label) || claim, modulo }];
    });
  });
}

/** Desembrulha o envelope do BFF (`{ success, data }`) até chegar ao recurso. */
function extractResource(payload: unknown): unknown {
  if (!isRecord(payload)) return payload;

  for (const key of ["data", "perfil"]) {
    const nested = payload[key];
    if (isRecord(nested)) return extractResource(nested);
  }

  return payload;
}

function toRole(raw: unknown): Role | null {
  if (!isRecord(raw)) return null;

  const id = readText(raw.id);
  const codigo = readFirstText(raw, "codigo", "code");
  if (!id && !codigo) return null;

  return {
    id: id || codigo,
    codigo,
    nome: readFirstText(raw, "nome", "name"),
    permissoes: extractRolePermissions(raw),
    // Contrato do BFF (`GET /api/plataforma/perfis`): só a contagem, nunca a lista de IDs.
    quantidadeUsuarios: readFirstCount(raw, "quantidadeUsuarios") ?? 0,
  };
}

export async function listRoles({
  pesquisa,
  claims,
  page,
  pageSize,
  sort = DEFAULT_ROLE_SORT,
  signal,
}: ListRolesParams = {}): Promise<RolesPage> {
  const term = pesquisa?.trim();
  const filtroClaims = (claims ?? []).filter((claim) => claim.trim() !== "");

  const params: Record<string, HttpQueryParam> = {};
  if (term) params.pesquisa = term;
  // Sempre lista: com um valor só em texto, o bff quebra ao juntar as claims (500).
  if (filtroClaims.length > 0) params.claims = filtroClaims;
  Object.assign(params, toPaginatedQuery({ page, pageSize, sort }));

  const config: HttpRequestConfig = { signal, params };

  const response = await getHttpClient().get<unknown>(ROLE_PATHS.LIST, config);

  // A ordem é a do serviço (`sort_by`), que ordena o conjunto inteiro antes de paginar.
  const roles = extractItems(response.data, COLLECTION_KEYS)
    .map(toRole)
    .filter((role): role is Role => role !== null);

  return { roles, total: extractTotal(response.data) ?? roles.length };
}

/**
 * Arquivo da visão em vigor (busca, permissões e ordenação), sem paginação: o serviço gera o arquivo e registra a
 * exportação na auditoria. O nome vem do `Content-Disposition`.
 */
export async function exportRoles({
  pesquisa,
  claims,
  sort = DEFAULT_ROLE_SORT,
  filtros,
  colunas,
  format,
  signal,
}: ExportRolesParams): Promise<ExportedFile> {
  const term = pesquisa?.trim();
  const filtroClaims = (claims ?? []).filter((claim) => claim.trim() !== "");
  const params: Record<string, HttpQueryParam> = {};
  if (term) params.pesquisa = term;
  if (filtroClaims.length > 0) params.claims = filtroClaims;
  Object.assign(params, toPaginatedQuery({ sort }));
  params.formato = format;
  appendExportColumns(params, colunas, ROLES_EXPORT_COLUMN_KEYS, "Perfis");
  appendReportQuery(params, filtros);

  const response = await getHttpClient().get<ArrayBuffer>(ROLE_PATHS.EXPORT, {
    signal,
    params,
    responseType: "arraybuffer",
  });

  return {
    content: new Uint8Array(response.data),
    fileName: parseContentDispositionFileName(response.headers?.["content-disposition"]),
    mimeType: response.headers?.["content-type"] ?? null,
  };
}

/** Corpo de gravação: `nome` e o conjunto completo de `claims`. */
function toRoleBody(nome: string, claims: string[]) {
  return {
    nome: nome.trim(),
    claims: claims.filter((claim) => claim.trim() !== ""),
  };
}

/**
 * Cadastra um perfil com o conjunto de permissões marcado. O código é gerado
 * pelo serviço e só volta a aparecer para o usuário na recarga da listagem.
 *
 * Retorna o perfil devolvido pela API ou `null` quando a resposta não traz
 * corpo utilizável (ex.: 201/204 sem conteúdo).
 */
export async function createRole({ nome, claims, signal }: CreateRoleParams): Promise<Role | null> {
  const response = await getHttpClient().post<unknown>(ROLE_PATHS.LIST, toRoleBody(nome, claims), {
    signal,
  });

  return toRole(extractResource(response.data));
}

/**
 * Atualiza o nome e as claims de um perfil. RN02: o serviço sincroniza a
 * associação, então `claims` precisa carregar o conjunto completo.
 */
export async function updateRole({
  id,
  nome,
  claims,
  signal,
}: UpdateRoleParams): Promise<Role | null> {
  const response = await getHttpClient().put<unknown>(buildRolePath(id), toRoleBody(nome, claims), {
    signal,
  });

  return toRole(extractResource(response.data));
}
/**
 * Exclui o perfil selecionado. A exclusão é lógica no serviço (RN02), mas para a
 * tela é definitiva: o registro deixa de aparecer na listagem.
 *
 * O serviço rejeita a operação quando o perfil ainda tem usuários vinculados
 * (RN03); o erro chega ao chamador para que a tela apresente o bloqueio.
 */
export async function deleteRole({ id, signal }: DeleteRoleParams): Promise<void> {
  await getHttpClient().delete<unknown>(buildRolePath(id), { signal });
}
